import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { contentApi, getErrorMessage, isAPIError, hasPeopleData } from '../services/api';
import { queryKeys } from '../services/queryClient';
import { ContentCard, APIResponse } from '../types';

// Hook for fetching featured content
export const useFeaturedContent = (options?: any) => {
  return useQuery<ContentCard[], Error>({
    queryKey: queryKeys.content.featured(),
    queryFn: async (): Promise<ContentCard[]> => {
      const result = await contentApi.getFeaturedContent();
      return result || [];
    },
    staleTime: 10 * 60 * 1000, // Featured content can be cached longer (10 minutes)
    retry: (failureCount: number, error: any) => {
      // Don't retry on authentication errors
      if (isAPIError(error) && error.response?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};

// Hook for fetching movies with pagination and filters
export const useMovies = (
  page = 1,
  limit = 20,
  filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; sortBy?: 'popularity' | 'year' | 'title'; popularTitleIds?: Set<string>; includePeople?: boolean },
  options?: any
) => {
  // Enhanced query key that includes people data flag for proper caching
  const enhancedQueryKey = [
    ...queryKeys.content.movies(page, filters),
    { includePeople: filters?.includePeople || false }
  ];

  return useQuery<APIResponse<ContentCard[]>, Error>({
    queryKey: enhancedQueryKey,
    queryFn: async () => {
      try {
        const result = await contentApi.getMovies(page, limit, filters);
        
        // Log people data statistics for debugging
        if (filters?.includePeople && result.data) {
          const moviesWithPeople = result.data.filter(hasPeopleData);
          console.log(`[useMovies] Loaded ${moviesWithPeople.length}/${result.data.length} movies with people data`);
        }
        
        return result;
      } catch (error) {
        // Enhanced error handling for people data failures
        if (filters?.includePeople && isAPIError(error)) {
          console.warn('[useMovies] People data request failed, falling back to content without people:', getErrorMessage(error));
          
          // Fallback: retry without people data to ensure core content still loads
          const fallbackFilters = { ...filters, includePeople: false };
          return contentApi.getMovies(page, limit, fallbackFilters);
        }
        throw error;
      }
    },
    keepPreviousData: true, // Keep previous data while loading new page
    staleTime: filters?.includePeople ? 10 * 60 * 1000 : 5 * 60 * 1000, // Longer cache for people data (10 min vs 5 min)
    cacheTime: filters?.includePeople ? 20 * 60 * 1000 : 10 * 60 * 1000, // Extended cache time for people data
    retry: (failureCount: number, error: any) => {
      // Don't retry on authentication errors
      if (isAPIError(error) && error.response?.status === 401) {
        return false;
      }
      
      // For people data requests, be more lenient with retries
      if (filters?.includePeople) {
        return failureCount < 2; // Fewer retries to avoid blocking UI
      }
      
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Faster retry for people data to improve perceived performance
      const baseDelay = filters?.includePeople ? 500 : 1000;
      return Math.min(baseDelay * 2 ** attemptIndex, 5000);
    },
    ...options,
  });
};

// Hook for infinite scrolling movies
export const useInfiniteMovies = (
  limit = 20,
  filters?: { genre?: string; year?: number },
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.content.movies(undefined, filters),
    queryFn: ({ pageParam = 1 }) => contentApi.getMovies(pageParam, limit, filters),
    getNextPageParam: (lastPage: APIResponse<ContentCard[]>, allPages: APIResponse<ContentCard[]>[]) => {
      if (!lastPage.pagination?.hasMore) return undefined;
      return allPages.length + 1;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Hook for fetching TV shows with pagination and filters
export const useTVShows = (
  page = 1,
  limit = 20,
  filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; sortBy?: 'popularity' | 'year' | 'title'; popularTitleIds?: Set<string>; includePeople?: boolean },
  options?: any
) => {
  // Enhanced query key that includes people data flag for proper caching
  const enhancedQueryKey = [
    ...queryKeys.content.tvShows(page, filters),
    { includePeople: filters?.includePeople || false }
  ];

  return useQuery<APIResponse<ContentCard[]>, Error>({
    queryKey: enhancedQueryKey,
    queryFn: async () => {
      try {
        const result = await contentApi.getTVShows(page, limit, filters);
        
        // Log people data statistics for debugging
        if (filters?.includePeople && result.data) {
          const showsWithPeople = result.data.filter(hasPeopleData);
          console.log(`[useTVShows] Loaded ${showsWithPeople.length}/${result.data.length} TV shows with people data`);
        }
        
        return result;
      } catch (error) {
        // Enhanced error handling for people data failures
        if (filters?.includePeople && isAPIError(error)) {
          console.warn('[useTVShows] People data request failed, falling back to content without people:', getErrorMessage(error));
          
          // Fallback: retry without people data to ensure core content still loads
          const fallbackFilters = { ...filters, includePeople: false };
          return contentApi.getTVShows(page, limit, fallbackFilters);
        }
        throw error;
      }
    },
    keepPreviousData: true,
    staleTime: filters?.includePeople ? 10 * 60 * 1000 : 5 * 60 * 1000, // Longer cache for people data (10 min vs 5 min)
    cacheTime: filters?.includePeople ? 20 * 60 * 1000 : 10 * 60 * 1000, // Extended cache time for people data
    retry: (failureCount: number, error: any) => {
      // Don't retry on authentication errors
      if (isAPIError(error) && error.response?.status === 401) {
        return false;
      }
      
      // For people data requests, be more lenient with retries
      if (filters?.includePeople) {
        return failureCount < 2; // Fewer retries to avoid blocking UI
      }
      
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Faster retry for people data to improve perceived performance
      const baseDelay = filters?.includePeople ? 500 : 1000;
      return Math.min(baseDelay * 2 ** attemptIndex, 5000);
    },
    ...options,
  });
};

// Hook for infinite scrolling TV shows
export const useInfiniteTVShows = (
  limit = 20,
  filters?: { genre?: string; year?: number },
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.content.tvShows(undefined, filters),
    queryFn: ({ pageParam = 1 }) => contentApi.getTVShows(pageParam, limit, filters),
    getNextPageParam: (lastPage: APIResponse<ContentCard[]>, allPages: APIResponse<ContentCard[]>[]) => {
      if (!lastPage.pagination?.hasMore) return undefined;
      return allPages.length + 1;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Hook for fetching content details
export const useContentDetails = (
  id: string,
  options?: any
) => {
  return useQuery({
    queryKey: queryKeys.content.details(id),
    queryFn: () => contentApi.getContentDetails(id),
    enabled: !!id, // Only run query if ID is provided
    staleTime: 15 * 60 * 1000, // Content details can be cached longer (15 minutes)
    ...options,
  });
};

// Hook for fetching content by genre
export const useContentByGenre = (
  genre: string,
  page = 1,
  limit = 20,
  options?: any
) => {
  return useQuery({
    queryKey: queryKeys.content.byGenre(genre, page),
    queryFn: () => contentApi.getContentByGenre(genre, page, limit),
    enabled: !!genre,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Hook for fetching content by year
export const useContentByYear = (
  year: number,
  page = 1,
  limit = 20,
  options?: any
) => {
  return useQuery({
    queryKey: queryKeys.content.byYear(year, page),
    queryFn: () => contentApi.getContentByYear(year, page, limit),
    enabled: !!year && year > 0,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Hook for fetching genres
export const useGenres = (options?: any) => {
  return useQuery({
    queryKey: queryKeys.genres.list(),
    queryFn: () => contentApi.getGenres(),
    staleTime: 30 * 60 * 1000, // Genres rarely change, cache for 30 minutes
    ...options,
  });
};

// Utility hook for handling query errors with user-friendly messages
export const useQueryError = () => {
  const formatError = (error: unknown): string => {
    if (isAPIError(error)) {
      const status = error.response?.status;
      const message = getErrorMessage(error);
      
      switch (status) {
        case 404:
          return 'Content not found. It may have been removed or is temporarily unavailable.';
        case 429:
          return 'Too many requests. Please wait a moment and try again.';
        case 500:
        case 502:
        case 503:
          return 'Server is temporarily unavailable. Please try again later.';
        default:
          return message;
      }
    }
    
    return getErrorMessage(error);
  };
  
  const isRetryableError = (error: unknown): boolean => {
    if (isAPIError(error)) {
      const status = error.response?.status;
      // Retry on server errors and rate limiting
      return status === 429 || (status !== undefined && status >= 500 && status < 600);
    }
    
    // Retry on network errors
    return !error || (error as any).code === 'NETWORK_ERROR';
  };
  
  return { formatError, isRetryableError };
};

// Hook for prefetching content details on hover
export const usePrefetchContentDetails = () => {
  const { prefetchQueries } = require('../services/queryClient');
  
  const prefetchContent = (id: string) => {
    if (id) {
      prefetchQueries.contentDetails(id);
    }
  };
  
  return { prefetchContent };
};

// Enhanced hook for movies with people data optimization
export const useMoviesWithPeople = (
  page = 1,
  limit = 20,
  filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; sortBy?: 'popularity' | 'year' | 'title'; popularTitleIds?: Set<string> },
  options?: any
) => {
  const enhancedFilters = { ...filters, includePeople: true };
  
  return useMovies(page, limit, enhancedFilters, {
    // Progressive loading: show content first, then enhance with people data
    placeholderData: (previousData: any) => {
      if (previousData?.data) {
        // Return previous data without people while loading new data with people
        return {
          ...previousData,
          data: previousData.data.map((item: ContentCard) => ({
            ...item,
            people: undefined, // Remove people data to show loading state
          })),
        };
      }
      return previousData;
    },
    ...options,
  });
};

// Enhanced hook for TV shows with people data optimization
export const useTVShowsWithPeople = (
  page = 1,
  limit = 20,
  filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; sortBy?: 'popularity' | 'year' | 'title'; popularTitleIds?: Set<string> },
  options?: any
) => {
  const enhancedFilters = { ...filters, includePeople: true };
  
  return useTVShows(page, limit, enhancedFilters, {
    // Progressive loading: show content first, then enhance with people data
    placeholderData: (previousData: any) => {
      if (previousData?.data) {
        // Return previous data without people while loading new data with people
        return {
          ...previousData,
          data: previousData.data.map((item: ContentCard) => ({
            ...item,
            people: undefined, // Remove people data to show loading state
          })),
        };
      }
      return previousData;
    },
    ...options,
  });
};

// Hook for managing people data cache and performance
export const usePeopleDataCache = () => {
  const { queryClient } = require('../services/queryClient');
  
  const prefetchPeopleData = (contentId: string, contentType: 'movie' | 'tv') => {
    // Prefetch people data for a specific content item
    const queryKey = contentType === 'movie' 
      ? [...queryKeys.content.movies(1, { includePeople: true }), contentId]
      : [...queryKeys.content.tvShows(1, { includePeople: true }), contentId];
    
    queryClient.prefetchQuery({
      queryKey,
      queryFn: async () => {
        // This would ideally be a dedicated endpoint for single content with people
        // For now, we rely on the existing cache
        return null;
      },
      staleTime: 15 * 60 * 1000, // 15 minutes for prefetched people data
    });
  };
  
  const invalidatePeopleData = () => {
    // Invalidate all queries that include people data
    queryClient.invalidateQueries({
      predicate: (query: any) => {
        const queryKey = query.queryKey as any[];
        return queryKey.some(key => 
          typeof key === 'object' && key?.includePeople === true
        );
      },
    });
  };
  
  const getCachedPeopleData = (contentId: string): ContentCard | null => {
    // Try to get people data from existing cache
    const movieCache = queryClient.getQueriesData({
      predicate: (query: any) => {
        const queryKey = query.queryKey as any[];
        return queryKey.includes('movies') && 
               queryKey.some(key => typeof key === 'object' && key?.includePeople === true);
      },
    });
    
    const tvCache = queryClient.getQueriesData({
      predicate: (query: any) => {
        const queryKey = query.queryKey as any[];
        return queryKey.includes('tv-shows') && 
               queryKey.some(key => typeof key === 'object' && key?.includePeople === true);
      },
    });
    
    // Search through cached data for the specific content
    const allCachedData = [...movieCache, ...tvCache];
    for (const [, data] of allCachedData) {
      if (data && typeof data === 'object' && 'data' in data) {
        const apiResponse = data as APIResponse<ContentCard[]>;
        const foundItem = apiResponse.data.find(item => item.id === contentId);
        if (foundItem && hasPeopleData(foundItem)) {
          return foundItem;
        }
      }
    }
    
    return null;
  };
  
  return {
    prefetchPeopleData,
    invalidatePeopleData,
    getCachedPeopleData,
  };
};

// Hook for handling people data loading states and errors
export const usePeopleDataState = (contentItems: ContentCard[]) => {
  const peopleDataStats = {
    total: contentItems.length,
    withPeople: contentItems.filter(hasPeopleData).length,
    withoutPeople: contentItems.filter(item => !hasPeopleData(item)).length,
    loadingProgress: contentItems.length > 0 
      ? (contentItems.filter(hasPeopleData).length / contentItems.length) * 100 
      : 0,
  };
  
  const isPeopleDataComplete = peopleDataStats.withPeople === peopleDataStats.total;
  const isPeopleDataPartial = peopleDataStats.withPeople > 0 && peopleDataStats.withPeople < peopleDataStats.total;
  const isPeopleDataEmpty = peopleDataStats.withPeople === 0;
  
  return {
    stats: peopleDataStats,
    isComplete: isPeopleDataComplete,
    isPartial: isPeopleDataPartial,
    isEmpty: isPeopleDataEmpty,
    loadingProgress: peopleDataStats.loadingProgress,
  };
};